import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import type { TrackingCore } from "./core";
import {
  recordOnboardingPrimaryEvent,
  recordOnboardingStage,
} from "../onboardingSession";

/** Where the shared SignInForm was shown when a sign-in event fired. */
export type AuthSurface = "onboarding" | "dialog";

/**
 * Onboarding & account-lifecycle events: the quiz, the paginated quiz stepper
 * funnel, the first-dinner welcome card, and the Clerk→Firebase/Apple account
 * migration flow. Merged onto the tracking singleton in index.ts.
 */
/**
 * The segments every onboarding event carries.
 *
 * A funnel that can only be read in aggregate answers almost nothing: the
 * question is always "for whom" — which community, in which language, from
 * which ad, in which arm of which experiment. These ride on every step event
 * so any step can be broken down by any of them without a join.
 *
 * Stable snake_case names, because PostHog insights and dashboards are built
 * against property names: add freely, rename nothing.
 */
export interface OnboardingContext {
  /** Flow arm — "v3", "control", or a future experiment arm within one. */
  variant: string;
  /** Community the flow is showing: men / sapphic / mixed. */
  audience: string;
  /** UI language at the time of the event ("en", "de", …). */
  language: string;
  /** Event type the ad promised (`et=`): "coffee" | "dinner" | null. */
  adEventType?: string | null;
  /** The ad pinned the community (`a=`), so the picker never ran. */
  communityPinned?: boolean;
  /** Age bracket, once the birthday has been asked. Null before Act 2. */
  ageBucket?: string | null;
  /** City id, once picked. Null before Act 1's picker. */
  city?: string | null;
  /** Signed in at the time of the event — the account screen splits the flow. */
  isSignedIn?: boolean;
}

function flowProps(ctx: OnboardingContext): Record<string, unknown> {
  return {
    variant: ctx.variant,
    audience: ctx.audience,
    language: ctx.language,
    ad_event_type: ctx.adEventType ?? "none",
    community_pinned: ctx.communityPinned ?? false,
    age_bucket: ctx.ageBucket ?? null,
    city: ctx.city ?? null,
    is_signed_in: ctx.isSignedIn ?? false,
  };
}

// Several methods below also beacon into the first-party onboarding session
// record (lib/onboardingSession). That channel is deliberately separate from
// analytics: it hits our own API so the session row stays correct when the
// PostHog capture is lost to consent or flag-loading races. The beacons no-op
// outside an active onboarding session.
export const onboardingEvents = {
  quizStarted(this: TrackingCore) {
    this.track("quiz_started");
    recordOnboardingPrimaryEvent("started");
  },

  quizQuestionAnswered(
    this: TrackingCore,
    questionId: string,
    questionIndex: number,
    category: string,
    totalQuestions: number,
  ) {
    this.track("quiz_question_answered", {
      question_id: questionId,
      question_index: questionIndex,
      question_category: category,
      total_questions: totalQuestions,
      progress_pct: Math.round(((questionIndex + 1) / totalQuestions) * 100),
    });
  },

  quizCompleted(this: TrackingCore) {
    this.track("quiz_completed");
    recordOnboardingPrimaryEvent("completed");

    // Fire native Facebook CompleteRegistration (quiz = onboarding completion)
    if (bridge.isNative) {
      bridge.emit(
        BridgeChannels.TRACKING,
        BridgeActions.TRACK_COMPLETED_REGISTRATION,
        { registrationMethod: "quiz" }
      );
    }
  },

  // ─── Paginated quiz stepper funnel ────────────────────────────────────────
  // Funnel events for the OnboardingQuiz (?stepper=1) experience. Every event
  // includes `audience` (men / sapphic / general) so we can read
  // conversion per ad-targeting variant.

  quizStepperExposed(this: TrackingCore, audience: string) {
    this.track("quiz_stepper_exposed", { audience });
  },

  quizStepperStepViewed(this: TrackingCore, args: {
    stepIndex: number;
    totalSteps: number;
    stepType: "question" | "interstitial" | "age-intro" | "community-intro";
    stepKey: string;
    section: string;
    audience: string;
  }) {
    this.track("quiz_stepper_step_viewed", {
      step_index: args.stepIndex,
      total_steps: args.totalSteps,
      step_type: args.stepType,
      step_key: args.stepKey,
      section: args.section,
      progress_pct: Math.round(((args.stepIndex + 1) / args.totalSteps) * 100),
      audience: args.audience,
    });
    // Viewing a stepper step means every step before it was cleared.
    recordOnboardingStage(`stepper:${args.stepKey}`);
  },

  quizStepperReachedAuthGate(this: TrackingCore, audience: string) {
    this.track("quiz_stepper_reached_auth_gate", { audience });
    recordOnboardingStage("stepper:auth_gate");
  },

  // `surface` is where the shared SignInForm was shown: the onboarding account
  // step, or the sign-in dialog on protected pages. The dialog reports through
  // the same events so one experiment metric covers both; filter on `surface`
  // to separate them.
  quizStepperAuthClicked(
    this: TrackingCore,
    provider: "apple" | "google" | "email" | "email_code",
    audience: string,
    surface: AuthSurface = "onboarding",
  ) {
    this.track("quiz_stepper_auth_clicked", { provider, audience, surface });
  },

  quizStepperAuthSucceeded(
    this: TrackingCore,
    provider: "apple" | "google" | "email" | "email_code",
    audience: string,
    surface: AuthSurface = "onboarding",
  ) {
    this.track("quiz_stepper_auth_succeeded", { provider, audience, surface });
    // Both flows funnel auth through this method (shared SignInForm), so this
    // is the one place "signed up inside onboarding" is known. The beacon
    // carries the fresh Firebase token, which is what links the session row
    // to the new user. A dialog sign-in is not an onboarding signup, even when
    // an old onboarding session id is still stored on the device.
    if (surface === "onboarding") recordOnboardingPrimaryEvent("signup");
  },

  // `errorCode` is the Firebase error code ("auth/popup-blocked", ...) or
  // "no-user" when the attempt resolved empty without throwing — a silently
  // closed popup, or a redirect that came back without a credential.
  // Distinguishes real failures from user cancellations so the
  // google_auth_redirect experiment's effect is attributable. The email_code
  // path reports its own: "send_failed", "account_not_found", and on verify the
  // server's reason ("invalid_code", "expired", "too_many_attempts",
  // "not_configured"), falling back to "verify_failed".
  quizStepperAuthFailed(
    this: TrackingCore,
    provider: "apple" | "google" | "email" | "email_code",
    audience: string,
    errorCode?: string | null,
    surface: AuthSurface = "onboarding",
  ) {
    this.track("quiz_stepper_auth_failed", {
      provider,
      audience,
      error_code: errorCode ?? "no-user",
      surface,
    });
  },

  // ─── v3 onboarding flow (components/onboarding) ───────────────────────────
  // Separate event names from the `quiz_stepper_*` family above: the two flows
  // have different screen sets, so folding them into one funnel would compare
  // steps that don't exist on both sides. `variant` carries the flow variant
  // (and any future A/B arm within it) so one experiment can be read per arm.
  //
  // Auth on the account screen still reports through quizStepperAuth* — that
  // surface is the shared SignInForm and those events already power the
  // provider-level experiments.

  /**
   * Which onboarding *experience* this person was shown. Fired by the route,
   * once per visit. Began as the exposure event of the `onboarding_flow`
   * experiment (v3 won, docs/experiments D-007) and is kept as the top of the
   * onboarding funnel.
   */
  onboardingExperienceShown(
    this: TrackingCore,
    experience: string,
    audience: string,
  ) {
    this.track("onboarding_experience_shown", {
      variant: experience,
      audience,
    });
  },

  onboardingFlowExposed(this: TrackingCore, ctx: OnboardingContext) {
    this.track("onboarding_flow_exposed", flowProps(ctx));
  },

  onboardingStepViewed(
    this: TrackingCore,
    args: OnboardingContext & {
      stepIndex: number;
      totalSteps: number;
      stepId: string;
      stepKind: string;
      act: string;
    },
  ) {
    this.track("onboarding_step_viewed", {
      ...flowProps(args),
      step_index: args.stepIndex,
      total_steps: args.totalSteps,
      step_id: args.stepId,
      step_kind: args.stepKind,
      act: args.act,
      progress_pct: Math.round(((args.stepIndex + 1) / args.totalSteps) * 100),
    });
  },

  onboardingStepCompleted(
    this: TrackingCore,
    args: OnboardingContext & {
      stepId: string;
      act: string;
      field: string;
    },
  ) {
    this.track("onboarding_step_completed", {
      ...flowProps(args),
      step_id: args.stepId,
      act: args.act,
      field: args.field,
    });
    recordOnboardingStage(`v3:${args.stepId}`);
  },

  /**
   * A deliberate way out of the flow: the offer's "browse first" link, and the
   * night picker's "I'll pick an event later". Measured because an escape
   * hatch is only defensible if we can see whether people come back through
   * it — and `escape_from` is what separates "not buying today" from "not
   * choosing a night today", which are different problems.
   */
  onboardingEscapeToEvents(
    this: TrackingCore,
    ctx: OnboardingContext,
    from: "offer" | "events" = "offer",
  ) {
    this.track("onboarding_escape_to_events", {
      ...flowProps(ctx),
      escape_from: from,
    });
  },

  onboardingFlowCompleted(this: TrackingCore, ctx: OnboardingContext) {
    this.track("onboarding_flow_completed", flowProps(ctx));
  },

  // ─── Guided post-signup booking (/booking/start experiment) ───────────────
  // `ad_event_type` is the type the user's ad landing targeted (coffee/dinner,
  // or "none" for a direct visit); `shown_event_type` is the type of the event
  // actually offered — they differ when no upcoming event of the ad type
  // existed and we fell back, which `is_fallback` marks explicitly.
  //
  // Event NAMES are load-bearing: the guided_post_signup_booking experiment
  // reads them. Add properties freely, but don't rename these once it runs.
  //
  // NOTE: `event_id` is reserved — core.track() overwrites it with the per-send
  // dedup id, so the eventTime id ships as `booking_event_id`.

  guidedBookingViewed(
    this: TrackingCore,
    args: {
      adEventType: string;
      shownEventType: string | null;
      isFallback: boolean;
      eventId: string;
      daysUntilEvent: number | null;
      city?: string | null;
    },
  ) {
    this.track("guided_booking_viewed", {
      ad_event_type: args.adEventType,
      shown_event_type: args.shownEventType,
      is_fallback: args.isFallback,
      booking_event_id: args.eventId,
      days_until_event: args.daysUntilEvent,
      city: args.city ?? null,
    });
  },

  guidedBookingCtaClicked(
    this: TrackingCore,
    args: {
      adEventType: string;
      action: "book" | "browse" | "skip";
      shownEventType: string | null;
      eventId: string;
    },
  ) {
    this.track("guided_booking_cta_clicked", {
      ad_event_type: args.adEventType,
      action: args.action,
      shown_event_type: args.shownEventType,
      booking_event_id: args.eventId,
    });
  },

  // The guided screen had nothing bookable to offer and bounced to /booking.
  // Without this the bail-out is invisible: no view event fires, so those
  // users look like they never reached the screen at all.
  guidedBookingUnavailable(this: TrackingCore, adEventType: string, city?: string | null) {
    this.track("guided_booking_unavailable", {
      ad_event_type: adEventType,
      city: city ?? null,
    });
  },

  // ─── First-dinner welcome card on /dashboard ──────────────────────────────

  firstDinnerWelcomeViewed(this: TrackingCore) {
    this.track("first_dinner_welcome_viewed");
  },

  firstDinnerWelcomeCtaClicked(this: TrackingCore) {
    this.track("first_dinner_welcome_cta_clicked");
  },

  firstDinnerWelcomeDetailOpened(this: TrackingCore) {
    this.track("first_dinner_welcome_detail_opened");
  },

  // ─── Account migration (Clerk → Firebase / Apple) ─────────────────────────

  migrationPageViewed(this: TrackingCore, hasPassword: boolean, isSignedIn: boolean) {
    this.track("migration_page_viewed", {
      has_password: hasPassword,
      is_signed_in: isSignedIn,
    });
  },

  migrationAppleSignInClicked(this: TrackingCore) {
    this.track("migration_apple_sign_in_clicked");
  },

  migrationStarted(this: TrackingCore) {
    this.track("migration_started");
  },

  migrationSuccess(this: TrackingCore) {
    this.track("migration_success");
  },

  migrationError(this: TrackingCore, error: string) {
    this.track("migration_error", {
      error_message: error,
    });
  },

  migrationGoToDashboardClicked(this: TrackingCore) {
    this.track("migration_go_to_dashboard_clicked");
  },

  emailVerificationResent(this: TrackingCore) {
    this.track("email_verification_resent");
  },
};
